/**
 * Zoom math for the paginated report viewer.
 *
 * The viewer auto-fits one page to the window (its own `scale` state, computed
 * from live layout). Zoom here is a user-controlled MULTIPLIER on top of that
 * fit, not a replacement for it: 100% (ZOOM_RESET) means "at the fit scale" -
 * today's default. Zooming out shrinks further past the fit (see more of the
 * page, or neighboring pages); zooming in magnifies past it (read fine print).
 * The viewer owns fit-scale measurement - these are pure functions only.
 *
 * State only ever moves by stepZoom starting from ZOOM_RESET, so it always
 * lands on the clean 10-point grid (50%, 60%, ... 200%) - no separate clamp
 * entry point is needed.
 */

export const ZOOM_MIN = 0.5;   // 50% of fit - see ~two pages at once
export const ZOOM_MAX = 2;     // 200% of fit - magnify to read fine print
export const ZOOM_STEP = 0.1;  // 10 percentage points per click
export const ZOOM_RESET = 1;   // 100% = the viewer's fit-page default

const EPS = 1e-9;

// Round to a clean 2-decimal grid to avoid 0.7999999999999999 float drift.
const round2 = (x) => Math.round(x * 100) / 100;

/**
 * Advance one click. `direction` > 0 zooms in, < 0 zooms out. Result is
 * rounded to the 2-decimal grid and clamped to [ZOOM_MIN, ZOOM_MAX].
 */
export function stepZoom(zoom, direction) {
  const next = round2(zoom + (direction > 0 ? ZOOM_STEP : -ZOOM_STEP));
  return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
}

/** Whether the zoom-in (+) control should be enabled. */
export function canZoomIn(zoom) {
  return zoom < ZOOM_MAX - EPS;
}

/** Whether the zoom-out (-) control should be enabled. */
export function canZoomOut(zoom) {
  return zoom > ZOOM_MIN + EPS;
}

/** Human-readable readout, e.g. 0.5 -> "50%". */
export function zoomPercent(zoom) {
  return `${Math.round(zoom * 100)}%`;
}
